import classNames from 'classnames';
import { useSearchParams } from 'hotel/navigation';

import FlightRosterTable from '../../components/FlightRosterTable';
import RosterTable from '../../components/RosterTable';
import { useAppSelector } from '../../store/store';

type TabId = 'hotels' | 'flights';

const panelId = (tab: TabId) =>
  tab === 'hotels' ? 'hotel-roster-panel' : 'flight-roster-panel';

export default function Roster() {
  const [searchParams, setSearchParams] = useSearchParams();
  const flightRosterEnabled =
    useAppSelector(
      (state) => state.user.profile?.client?.clientPortalFlightRosterEnabled,
    ) ?? false;
  if (!flightRosterEnabled) {
    return <RosterTable />;
  }

  const tabs: Array<{ id: TabId; label: string }> = [
    { id: 'hotels', label: 'Hotel Roster' },
    { id: 'flights', label: 'Flight Roster' },
  ];
  const requestedTab = searchParams.get('tab');
  const activeTab: TabId = tabs.some((tab) => tab.id === requestedTab)
    ? (requestedTab as TabId)
    : 'hotels';

  const setActiveTab = (tab: TabId) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'hotels') {
      next.delete('tab');
    } else {
      next.set('tab', tab);
    }
    setSearchParams(next);
  };

  return (
    <div className="flex flex-col">
      <div className="px-5 pt-5 sm:px-10">
        <div className="border-b border-gray-200">
          <div
            aria-label="Roster views"
            className="-mb-px flex space-x-8"
            role="tablist"
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                aria-controls={panelId(tab.id)}
                aria-selected={activeTab === tab.id}
                id={`${tab.id}-roster-tab`}
                role="tab"
                type="button"
                className={classNames(
                  activeTab === tab.id
                    ? 'border-accent text-accent'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                  'cursor-pointer whitespace-nowrap border-b-2 bg-transparent px-1 py-4 text-sm font-medium',
                )}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div
        aria-labelledby={`${activeTab}-roster-tab`}
        id={panelId(activeTab)}
        role="tabpanel"
      >
        {activeTab === 'hotels' ? <RosterTable /> : <FlightRosterTable />}
      </div>
    </div>
  );
}
